import type { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";
import { getAnalyticsEnvironment } from "@/lib/analytics/environment";
import { getAnalyticsLocation } from "@/lib/analytics/urlSanitization";

type LogLevel = "debug" | "info" | "warn" | "error" | "fatal";

const isBrowser = () => typeof window !== "undefined";

const telemetryEnvironment = getAnalyticsEnvironment();

const rawConnectionString = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING?.trim();
const customEndpointUrl = import.meta.env.VITE_APPLICATIONINSIGHTS_ENDPOINT_URL?.trim();
const isDisabled = !rawConnectionString || rawConnectionString === "disabled";

if (isBrowser() && telemetryEnvironment === "local") {
  if (isDisabled) {
    console.info(
      "[Telemetry] VITE_APPLICATIONINSIGHTS_CONNECTION_STRING is not set; Application Insights logging will remain disabled.",
    );
  } else {
    console.info(
      "[Telemetry] VITE_APPLICATIONINSIGHTS_CONNECTION_STRING is set; Application Insights logging enabled.",
    );
  }
}

let appInsightsInstance: ApplicationInsights | null = null;
let listenersBound = false;
let flushListenersBound = false;
let severityMap: Record<LogLevel, SeverityLevel> | null = null;
let appInsightsInitializationPromise: Promise<ApplicationInsights | null> | null = null;
let appInsightsInitializationFailed = false;

type AppInsightsModule = typeof import("@microsoft/applicationinsights-web");

let appInsightsModulePromise: Promise<AppInsightsModule> | null = null;

const loadAppInsightsModule = () => {
  if (!appInsightsModulePromise) {
    appInsightsModulePromise = import("@microsoft/applicationinsights-web");
  }

  return appInsightsModulePromise;
};

const MAX_TELEMETRY_TEXT_LENGTH = 1_000;
const MAX_TELEMETRY_STACK_LENGTH = 8_000;
const MAX_TELEMETRY_PROPERTY_COUNT = 32;
const MAX_TELEMETRY_PROPERTY_KEY_LENGTH = 100;
const SAFE_TELEMETRY_PROPERTY_KEY = /^[A-Za-z][A-Za-z0-9_.-]*$/;
const SENSITIVE_PROPERTY_KEY =
  /(?:authorization|cookie|credential|password|secret|token|api[-_]?key|access[-_]?key|email|user[-_]?id|session[-_]?id|tenant[-_]?id|anonymous[-_]?id)/i;
// Upload-library identifiers and user-supplied names are sensitive, while the lowercase
// `filename` attached by window.onerror is a sanitized source URL and remains operationally useful.
const SENSITIVE_UPLOAD_FILE_PROPERTY_KEY = /(?:file(?:Name|Id)|file[-_](?:name|id))/;
const HTTP_URL_PATTERN = /https?:\/\/[^\s"'<>]+/gi;
const RELATIVE_URL_QUERY_PATTERN = /(^|[\s("'`])((?:\/|\.\.?\/)[^\s"'<>?#]*)[?#][^\s"'<>]*/gm;
const SENSITIVE_ASSIGNMENT_PATTERN =
  /\b(authorization|cookie|credential|password|secret|token|access[_-]?token|refresh[_-]?token|api[_-]?key|oauth[_-]?code|code[_-]?verifier|state)\b(\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^\s,;]+)/gi;
const BEARER_TOKEN_PATTERN = /\bbearer\s+[^\s,;]+/gi;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

// This site's bundled script names a build file, never a user or a token, and its line and column
// are what trace a minified stack to the source. Another origin's path could hold anything. The
// closing parenthesis is a Chrome stack frame's, swept up by HTTP_URL_PATTERN.
const BUNDLED_SCRIPT_PATH = /^\/assets\/[\w.-]+\.m?js(?::\d+:\d+)?\)?$/;

const sanitizeTelemetryUrl = (value: string): string => {
  try {
    const base = typeof window === "undefined" ? "https://telemetry.invalid" : window.location.origin;
    const parsed = new URL(value, base);
    if (parsed.origin === base && BUNDLED_SCRIPT_PATH.test(parsed.pathname))
      return `${parsed.origin}${parsed.pathname}`;
    return getAnalyticsLocation(value, base)?.url ?? "";
  } catch {
    return "";
  }
};

const replaceUnsafeControlCharacters = (value: string): string =>
  Array.from(value, (character) => {
    const codePoint = character.codePointAt(0);
    if (codePoint === undefined) return " ";
    const isAllowedWhitespace = codePoint === 0x09 || codePoint === 0x0a || codePoint === 0x0d;
    const isControlCharacter = codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f);
    return isControlCharacter && !isAllowedWhitespace ? " " : character;
  }).join("");

const sanitizeTelemetryText = (value: string, maxLength = MAX_TELEMETRY_TEXT_LENGTH): string => {
  // Bound work before applying regular expressions. Exception messages and user-defined stacks are
  // hostile inputs just like request payloads; operational telemetry must never amplify them.
  const boundedInput = value.slice(0, Math.max(maxLength * 4, maxLength));
  const sanitized = boundedInput
    .replace(HTTP_URL_PATTERN, (url) => sanitizeTelemetryUrl(url) || "[url]")
    .replace(RELATIVE_URL_QUERY_PATTERN, (_match, prefix: string, path: string) => `${prefix}${path}`)
    .replace(SENSITIVE_ASSIGNMENT_PATTERN, (_match, key: string, separator: string) => `${key}${separator}[redacted]`)
    .replace(BEARER_TOKEN_PATTERN, "Bearer [redacted]")
    .replace(EMAIL_PATTERN, "[email]");
  // Preserve tabs/newlines in stacks while removing other control characters.
  return replaceUnsafeControlCharacters(sanitized).slice(0, maxLength);
};

const telemetryValueType = (value: unknown): string => {
  if (value instanceof Error) return sanitizeTelemetryText(value.name, 100) || "Error";
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
};

const describeTelemetryValue = (value: unknown): string => {
  if (value == null) {
    return "";
  }

  if (typeof value === "string") {
    return sanitizeTelemetryText(value);
  }

  if (typeof value === "number" || typeof value === "bigint" || typeof value === "boolean") {
    return String(value);
  }

  if (value instanceof Error) {
    return sanitizeTelemetryText(`${sanitizeTelemetryText(value.name, 100)}: ${value.message}`);
  }

  if (typeof value === "object") {
    // Never inspect or stringify arbitrary objects. They can contain credentials, cyclic graphs,
    // hostile getters, or an unbounded response body.
    return Array.isArray(value) ? "[array]" : "[object]";
  }

  if (typeof value === "function") return "[function]";
  if (typeof value === "symbol") return "[symbol]";
  return "";
};

const toTelemetryProperties = (
  properties?: Record<string, unknown>,
  maxPropertyCount = MAX_TELEMETRY_PROPERTY_COUNT,
) => {
  if (!properties) return undefined;

  const sanitized: Record<string, string> = {};
  try {
    for (const key of Object.keys(properties)) {
      if (Object.keys(sanitized).length >= maxPropertyCount) break;
      const sanitizedKey = key.trim().slice(0, MAX_TELEMETRY_PROPERTY_KEY_LENGTH);
      if (
        !sanitizedKey ||
        !SAFE_TELEMETRY_PROPERTY_KEY.test(sanitizedKey) ||
        SENSITIVE_PROPERTY_KEY.test(sanitizedKey) ||
        SENSITIVE_UPLOAD_FILE_PROPERTY_KEY.test(sanitizedKey)
      ) {
        continue;
      }

      let value: unknown;
      try {
        value = properties[key];
      } catch {
        continue;
      }
      if (value === undefined) continue;
      sanitized[sanitizedKey] = describeTelemetryValue(value);
    }
  } catch {
    return undefined;
  }

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
};

const buildSeverityMap = (levels: AppInsightsModule["SeverityLevel"]): Record<LogLevel, SeverityLevel> => ({
  debug: levels.Verbose,
  info: levels.Information,
  warn: levels.Warning,
  error: levels.Error,
  fatal: levels.Critical,
});

const initializeAppInsightsOnce = async (): Promise<ApplicationInsights | null> => {
  if (!isBrowser() || isDisabled) {
    return null;
  }

  if (appInsightsInstance) {
    return appInsightsInstance;
  }

  const { ApplicationInsights, SeverityLevel } = await loadAppInsightsModule();

  severityMap = severityMap ?? buildSeverityMap(SeverityLevel);

  const instance = new ApplicationInsights({
    config: {
      connectionString: rawConnectionString,
      // Optional override to route ingestion to a self-hosted/proxied endpoint
      ...(customEndpointUrl && /^https:\/\//i.test(customEndpointUrl) ? { endpointUrl: customEndpointUrl } : {}),
      // Keep auto-collection minimal; rely on explicit logging + filters.
      // Errors and unhandled rejections are tracked by our own window listeners
      // (bindGlobalListeners); the SDK equivalents (enableUnhandledPromiseRejectionTracking,
      // enableCorsCorrelation) are inert with exception/ajax/fetch tracking disabled.
      disableAjaxTracking: true,
      disableFetchTracking: true,
      disableExceptionTracking: true,
      enableAutoRouteTracking: false,
      autoTrackPageVisitTime: false,
      // Never use cookies to avoid identifiers pre/post consent
      disableCookiesUsage: true,
    },
  });

  instance.loadAppInsights();

  const cloudRoleTag = "ai.cloud.role";
  instance.addTelemetryInitializer((envelope) => {
    envelope.tags = envelope.tags ?? {};
    envelope.tags[cloudRoleTag] = `evangelist:exchange:frontend:${telemetryEnvironment}`;
  });

  // Initializers receive ITelemetryItem, before the sender wraps baseType/baseData in data.
  // Reading the serialized envelope shape here silently drops every exception.
  instance.addTelemetryInitializer((item) => {
    try {
      const baseData: Record<string, unknown> | undefined = item.baseData;
      if (!baseData) return false;
      const isAllowedEvent = item.baseType === "EventData" && isOperationalEventName(baseData.name);
      if (item.baseType !== "ExceptionData" && !isAllowedEvent) return false;

      // Scrub common tags
      const tagKeys = ["ai.user.id", "ai.user.accountId", "ai.session.id", "ai.user.authUserId", "ai.operation.name"];
      item.tags = item.tags ?? {};
      for (const key of tagKeys) delete item.tags[key];

      // The SDK adds these extensions even with cookies disabled, then converts them to tags
      // AFTER initializers run. Scrubbing only item.tags would reintroduce generated identities
      // and the unsanitized page pathname in the serialized exception.
      const extensions: Record<string, unknown> | undefined = item.ext;
      if (extensions) {
        delete extensions.user;
        if (isTelemetryRecord(extensions.app)) delete extensions.app.sesId;
        if (isTelemetryRecord(extensions.trace)) delete extensions.trace.name;
      }

      // Sanitize every property again at the final SDK boundary. This protects against future
      // callers bypassing trackException's normalization and keeps the envelope bounded.
      const props = baseData.properties;
      baseData.properties = toTelemetryProperties(isTelemetryRecord(props) ? props : undefined) ?? {};
      item.data = toTelemetryProperties(item.data) ?? {};

      return true;
    } catch {
      return false;
    }
  });

  appInsightsInstance = instance;
  bindFlushListeners(instance);
  return instance;
};

const isTelemetryRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const initializeAppInsights = (): Promise<ApplicationInsights | null> => {
  if (!isBrowser() || isDisabled) return Promise.resolve(null);
  if (appInsightsInstance) return Promise.resolve(appInsightsInstance);
  if (appInsightsInitializationFailed) return Promise.resolve(null);
  if (appInsightsInitializationPromise) return appInsightsInitializationPromise;

  const initialization = initializeAppInsightsOnce()
    .catch((error: unknown) => {
      // Never send telemetry-pipeline failures back through the same failed pipeline.
      console.error("[Telemetry] Failed to initialize Application Insights", describeTelemetryValue(error));
      appInsightsInitializationFailed = true;
      return null;
    })
    .finally(() => {
      if (!appInsightsInstance && appInsightsInitializationPromise === initialization) {
        appInsightsInitializationPromise = null;
      }
    });
  appInsightsInitializationPromise = initialization;
  return initialization;
};

// A cross-origin script (third-party analytics/embeds/wallet SDKs loaded without CORS) that throws is
// reported by the browser with all detail withheld: no error object, no filename, no stack — just the
// literal message "Script error." These carry zero actionable information, so we keep them out of
// exception telemetry rather than flooding it with un-debuggable noise.
export const isOpaqueCrossOriginScriptError = (event: Pick<ErrorEvent, "error" | "filename" | "message">) =>
  !event.error && !event.filename && /^script error\.?$/i.test(event.message ?? "");

const bindGlobalListeners = () => {
  if (!isBrowser() || listenersBound) {
    return;
  }

  listenersBound = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    // Ignore errors from browser extensions — they are not actionable
    if (event.filename?.startsWith("chrome-extension://") || event.filename?.startsWith("moz-extension://")) {
      return;
    }

    // Opaque cross-origin "Script error." events carry no actionable detail — keep them out of telemetry.
    if (isOpaqueCrossOriginScriptError(event)) {
      return;
    }

    trackException(event.error ?? event.message, {
      source: "window.onerror",
      level: "error",
      properties: {
        filename: event.filename ? sanitizeTelemetryUrl(event.filename) : undefined,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    // Ignore rejections originating in browser extensions — they are not actionable.
    // Mirrors the filter on the `error` handler above.
    const reason: unknown = event.reason;
    if (
      reason instanceof Error &&
      typeof reason.stack === "string" &&
      (reason.stack.includes("chrome-extension://") || reason.stack.includes("moz-extension://"))
    ) {
      return;
    }

    trackException(reason ?? "unhandledrejection: missing reason", {
      source: "window.unhandledrejection",
      level: "error",
      properties: {
        reasonType: telemetryValueType(reason),
      },
    });
  });
};

const bindFlushListeners = (client: ApplicationInsights) => {
  if (!isBrowser() || flushListenersBound) {
    return;
  }

  const flush = () => {
    try {
      void Promise.resolve(client.flush(true)).catch(() => {
        // A rejected flush must not become an unload-time unhandled rejection.
      });
    } catch {
      // Unload telemetry is best-effort and must never block navigation.
    }
  };

  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  });

  flushListenersBound = true;
};

const logToConsole = (level: LogLevel, message: string, details?: unknown) => {
  const prefix = "[Telemetry]";
  const args = details === undefined ? [prefix, message] : [prefix, message, details];

  switch (level) {
    case "debug":
      console.debug(...args);
      break;
    case "info":
      console.info(...args);
      break;
    case "warn":
      console.warn(...args);
      break;
    case "error":
    case "fatal":
    default:
      console.error(...args);
      break;
  }
};

type ExceptionOptions = {
  level?: LogLevel;
  source?: string;
  properties?: Record<string, unknown>;
  suppressConsole?: boolean;
};

export function trackException(
  error: unknown,
  { level = "error", source, properties, suppressConsole = false }: ExceptionOptions = {},
) {
  const isErrorInstance = error instanceof Error;
  const message = isErrorInstance
    ? sanitizeTelemetryText(error.message)
    : typeof error === "string"
      ? sanitizeTelemetryText(error)
      : "Unknown error";
  const err = new Error(message);
  if (isErrorInstance) {
    err.name = sanitizeTelemetryText(error.name, 100) || "Error";
    // Safari and Firefox leave a failed fetch's stack empty; `err`'s own would name this function.
    err.stack = error.stack
      ? sanitizeTelemetryText(error.stack, MAX_TELEMETRY_STACK_LENGTH)
      : `${err.name}: ${message}`;
  }

  const normalizedProperties: Record<string, string> = {
    // Reserve the final slots for environment, source, and non-Error type metadata.
    ...(toTelemetryProperties(properties, MAX_TELEMETRY_PROPERTY_COUNT - 3) ?? {}),
    env: sanitizeTelemetryText(telemetryEnvironment, 100),
  };

  if (!isErrorInstance && error !== undefined) {
    normalizedProperties.originalErrorType = telemetryValueType(error);
  }

  if (!suppressConsole) {
    logToConsole(level, source ?? err.message, { error: err, ...normalizedProperties });
  }

  // The fixed browser policy is operational errors only. Keep lower-severity direct calls local
  // even if a future caller bypasses the frontend logger's equivalent guard.
  if (level !== "error" && level !== "fatal") return;

  void initializeAppInsights()
    .then((client) => {
      if (!client || !severityMap) return;
      client.trackException({
        exception: err,
        severityLevel: severityMap[level],
        properties: {
          ...(source ? { source: sanitizeTelemetryText(source) } : {}),
          ...normalizedProperties,
        },
      });
    })
    .catch(() => {
      // A telemetry failure must never create another unhandled rejection.
    });
}

/**
 * The only events the browser sends besides exceptions: measurements ops needs and nothing else carries.
 * Each one is scrubbed like an exception (no user, session or page identity) and carries only the
 * properties its caller passes. Adding a name here widens what leaves the browser, so it is a decision.
 */
const OPERATIONAL_EVENT_NAMES = ["upload.succeeded"] as const;
type OperationalEventName = (typeof OPERATIONAL_EVENT_NAMES)[number];

const isOperationalEventName = (name: unknown): name is OperationalEventName =>
  typeof name === "string" && (OPERATIONAL_EVENT_NAMES as readonly string[]).includes(name);

/** Sends one of `OPERATIONAL_EVENT_NAMES` with the given properties (sanitized, bounded) and the environment. */
export function trackOperationalEvent(name: OperationalEventName, properties: Record<string, unknown> = {}) {
  const normalizedProperties: Record<string, string> = {
    ...(toTelemetryProperties(properties, MAX_TELEMETRY_PROPERTY_COUNT - 1) ?? {}),
    env: sanitizeTelemetryText(telemetryEnvironment, 100),
  };

  void initializeAppInsights()
    .then((client) => {
      if (!client) return;
      client.trackEvent({ name }, normalizedProperties);
    })
    .catch(() => {
      // A telemetry failure must never create another unhandled rejection.
    });
}

// Essential operational telemetry initializes independently from marketing consent.
if (isBrowser() && !isDisabled) {
  if (!listenersBound) bindGlobalListeners();
  void initializeAppInsights();
}
